import { api } from '../api.js';
import { auth } from '../auth.js';
import { escapeHtml, showToast } from '../utils.js';
import { openModal } from './events.js';
import {
    addMonths, dateKey, gridWindow, monthGridDays, parseDateOnly, placeOccurrences,
    rruleFromRepeat, startOfMonth, toApiTimes, toLocalInputValue,
} from '../calendar-model.js';
import {
    monthTitle, renderDay, renderEventDetail, renderEventForm, renderList, renderMonth, renderSubscribe,
} from '../calendar-view.js';

/**
 * The family calendar: a month grid and a list, an event form with a visibility picker, and the
 * panel that hands out the private address a calendar app subscribes to.
 *
 * Dates and markup live in calendar-model.js and calendar-view.js, which have no DOM and are
 * tested under Node. This file only fetches, holds state and attaches listeners.
 */

const VIEW_KEY = 'calendarView';

/**
 * The month grid works on a phone but is cramped (a chip has room for about two letters), so a
 * narrow screen opens on the list unless the person has picked a view before. The same width the
 * stylesheet switches its compact layout at.
 */
function defaultView() {
    const stored = localStorage.getItem(VIEW_KEY);
    if (stored === 'month' || stored === 'list') return stored;
    return window.matchMedia('(max-width: 720px)').matches ? 'list' : 'month';
}

/** One page's worth of state. Rebuilt on every render, so nothing leaks from one visit to the next. */
let state;

export async function renderCalendar() {
    state = {
        cursor: startOfMonth(new Date()),
        view: defaultView(),
        events: [],
        people: null,
        token: undefined,
        // Bumped for every fetch so a slow answer for a month the user has already left cannot overwrite the current one.
        loadId: 0,
    };

    const app = document.getElementById('app');
    app.innerHTML = `
        <div class="container cal-page">
            <div class="page-header">
                <div>
                    <h1>Calendar</h1>
                    <p>The family's events in one place, in your calendar app too.</p>
                </div>
                <button type="button" id="cal-new" class="btn btn-primary"><i class="fa-solid fa-plus"></i> New event</button>
            </div>
            <div class="cal-toolbar">
                <div class="cal-nav">
                    <button type="button" class="btn btn-secondary btn-sm" id="cal-prev" aria-label="Previous month"><i class="fa-solid fa-chevron-left"></i></button>
                    <button type="button" class="btn btn-secondary btn-sm" id="cal-today">Today</button>
                    <button type="button" class="btn btn-secondary btn-sm" id="cal-next" aria-label="Next month"><i class="fa-solid fa-chevron-right"></i></button>
                    <h2 class="cal-title" id="cal-month-title" aria-live="polite"></h2>
                </div>
                <div class="cal-view-toggle" role="group" aria-label="View">
                    <button type="button" class="btn btn-secondary btn-sm" data-view="month">Month</button>
                    <button type="button" class="btn btn-secondary btn-sm" data-view="list">List</button>
                </div>
            </div>
            <div id="cal-body" class="cal-body"><p class="text-muted">Loading the calendar…</p></div>
            <section class="cal-subscribe" aria-labelledby="cal-subscribe-heading">
                <h2 id="cal-subscribe-heading"><i class="fa-solid fa-rss"></i> Subscribe in your calendar</h2>
                <div id="cal-subscribe-content"><p class="text-muted">Loading…</p></div>
            </section>
        </div>`;

    app.querySelector('#cal-new').addEventListener('click', () => openForm(null, { day: new Date() }));
    app.querySelector('#cal-prev').addEventListener('click', () => moveMonth(-1));
    app.querySelector('#cal-next').addEventListener('click', () => moveMonth(1));
    app.querySelector('#cal-today').addEventListener('click', () => {
        state.cursor = startOfMonth(new Date());
        void load();
    });
    app.querySelectorAll('[data-view]').forEach((button) => button.addEventListener('click', () => {
        state.view = button.dataset.view;
        try { localStorage.setItem(VIEW_KEY, state.view); } catch { /* a preference, not worth failing over */ }
        draw();
    }));
    // One listener for every chip, row, day number and action button, however often the body is redrawn.
    app.querySelector('#cal-body').addEventListener('click', onBodyClick);
    app.querySelector('#cal-subscribe-content').addEventListener('click', onSubscribeClick);

    // Home's empty "nothing in the next two weeks" panel links here with ?new=1, so the
    // person who clicked it lands on the form rather than on an empty month to hunt through.
    // Not awaited: the form fetches the family list itself, and the month behind it should
    // keep loading meanwhile.
    if (new URLSearchParams(window.location.search).get('new')) {
        void openForm(null, { day: new Date() });
    }

    await Promise.all([load(), loadToken()]);
}

function moveMonth(delta) {
    state.cursor = addMonths(state.cursor, delta);
    void load();
}

async function load() {
    const myLoad = ++state.loadId;
    const { from, to } = gridWindow(state.cursor);
    document.getElementById('cal-month-title').textContent = monthTitle(state.cursor);
    try {
        const { events } = await api.listCalendarEvents(from, to);
        if (myLoad !== state.loadId) return;
        state.events = events;
        draw();
    } catch (err) {
        if (myLoad !== state.loadId) return;
        document.getElementById('cal-body').innerHTML = `
            <div class="empty-state">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <h2>Couldn't load the calendar</h2>
                <p>${escapeHtml(err.message)}</p>
            </div>`;
    }
}

function draw() {
    const body = document.getElementById('cal-body');
    if (!body) return;
    const days = monthGridDays(state.cursor);
    const viewerId = auth.getUser()?.id;
    const byDay = placeOccurrences(state.events, days);

    document.querySelectorAll('[data-view]').forEach((b) => b.classList.toggle('is-active', b.dataset.view === state.view));
    document.getElementById('cal-month-title').textContent = monthTitle(state.cursor);

    if (state.view === 'list') {
        const inMonth = days.filter((d) => d.getMonth() === state.cursor.getMonth());
        body.innerHTML = renderList(inMonth, byDay, { viewerId });
    } else {
        body.innerHTML = renderMonth(state.cursor, days, byDay, { today: new Date(), viewerId });
    }
}

// ---------------------------------------------------------------------------- Clicks

function onBodyClick(e) {
    const target = e.target.closest('[data-event], [data-add], [data-day], [data-download]');
    if (!target) return;

    if (target.dataset.download) {
        e.stopPropagation();
        void downloadIcs(Number(target.dataset.download));
    } else if (target.dataset.event) {
        void openDetail(Number(target.dataset.event), { starts_at: target.dataset.start, ends_at: target.dataset.end });
    } else if (target.dataset.add) {
        openForm(null, { day: parseDateOnly(target.dataset.add) });
    } else if (target.dataset.day) {
        openDay(target.dataset.day);
    }
}

/** Everything on one day, when the grid could only fit a few of it. */
function openDay(key) {
    const day = parseDateOnly(key);
    const entries = placeOccurrences(state.events, [day]).get(key) ?? [];
    const { body, close } = openModal(day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }), renderDay(day, entries, { viewerId: auth.getUser()?.id }));
    body.addEventListener('click', (e) => {
        const target = e.target.closest('[data-event], [data-download]');
        if (!target) return;
        if (target.dataset.download) return void downloadIcs(Number(target.dataset.download));
        close();
        void openDetail(Number(target.dataset.event), { starts_at: target.dataset.start, ends_at: target.dataset.end });
    });
}

// ---------------------------------------------------------------------------- People

/** Who an event can be shared with. Empty for a limited account, which is refused the list. */
async function people() {
    if (state.people) return state.people;
    if (auth.isLimited()) return (state.people = []);
    try {
        state.people = (await api.listShareTargets()).users ?? [];
    } catch {
        // Only the names in the visibility line and the picker are lost, and the picker is
        // rebuilt on the next open. Not worth blocking the event on.
        return [];
    }
    return state.people;
}

// ---------------------------------------------------------------------------- Detail

async function openDetail(id, occurrence) {
    let event;
    try {
        [event] = await Promise.all([api.getCalendarEvent(id), people()]);
    } catch (err) {
        showToast(err.message ?? 'Could not open that event', 'error');
        return;
    }

    const { body, close } = openModal(event.title, renderEventDetail(event, {
        occurrence, people: state.people ?? [], viewerId: auth.getUser()?.id,
    }));

    body.addEventListener('click', async (e) => {
        const button = e.target.closest('button');
        if (!button) return;
        if (button.dataset.download) {
            void downloadIcs(id);
        } else if (button.dataset.edit) {
            close();
            void openForm(event, {});
        } else if (button.dataset.delete) {
            const series = event.rrule ? ' This deletes every occurrence, not just this one.' : '';
            if (!confirm(`Delete "${event.title}"?${series} This cannot be undone.`)) return;
            try {
                await api.deleteCalendarEvent(id);
                close();
                showToast('Event deleted', 'success');
                await load();
            } catch (err) {
                showToast(err.message ?? 'Could not delete the event', 'error');
            }
        }
    });
}

// ---------------------------------------------------------------------------- Form

/** `event` is null for a new one. `day` is the day whose "+" or number was pressed, or today. */
async function openForm(event, { day }) {
    const limited = auth.isLimited();
    const list = await people();

    const start = new Date(day ?? new Date());
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60_000);
    const defaults = {
        allDay: false,
        start: toLocalInputValue(start),
        end: toLocalInputValue(end),
    };

    const { body, close } = openModal(event ? 'Edit event' : 'New event', renderEventForm({ event, defaults, people: list, limited }), { wide: true });
    const $ = (selector) => body.querySelector(selector);

    body.querySelector('#cal-cancel').addEventListener('click', close);
    wireAllDayToggle(body);
    wireVisibility(body);

    $('#cal-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const error = $('#cal-error');
        error.classList.add('hidden');

        const submit = $('button[type="submit"]');
        const fail = (message) => {
            error.textContent = message;
            error.classList.remove('hidden');
            submit.disabled = false;
        };

        const title = $('#cal-title').value.trim();
        const allDay = $('#cal-all-day').checked;
        const startValue = $('#cal-start').value;
        const endValue = $('#cal-end').value;
        const until = $('#cal-until').value;
        const custom = $('#cal-repeat').disabled;

        if (!title) return fail('Give the event a title.');
        if (!startValue || !endValue) return fail(allDay ? 'Pick the first and last day.' : 'Pick a start and an end.');
        const ordered = allDay ? endValue >= startValue : new Date(endValue) > new Date(startValue);
        if (!ordered) return fail(allDay ? 'The last day cannot be before the first.' : 'The end must be after the start.');
        if (until && until < (allDay ? startValue : dateKey(new Date(startValue)))) return fail('"Repeat until" cannot be before the event starts.');

        submit.disabled = true;
        const payload = {
            title,
            description: $('#cal-description').value,
            location: $('#cal-location').value,
            all_day: allDay,
            ...toApiTimes({ allDay, start: startValue, end: endValue }),
            // A rule the presets cannot express is sent back untouched rather than flattened.
            rrule: custom ? event.rrule : rruleFromRepeat({ repeat: $('#cal-repeat').value, until, allDay, start: startValue }),
        };
        if (!limited) {
            payload.shared = $('#cal-shared').checked;
            payload.share_user_ids = [...body.querySelectorAll('.share-person-check:checked')].map((box) => Number(box.value));
        }

        try {
            if (event) await api.updateCalendarEvent(event.id, payload);
            else await api.createCalendarEvent(payload);
            close();
            showToast(event ? 'Event updated' : 'Event added', 'success');
            await load();
        } catch (err) {
            fail(err.message ?? 'Could not save the event');
        }
    });
}

/**
 * Switching between a date and a date-and-time keeps what was typed, as far as it can:
 * a timed event becomes the whole of its day, and an all-day one becomes a 09:00 to 10:00 slot.
 */
function wireAllDayToggle(root) {
    const toggle = root.querySelector('#cal-all-day');
    const start = root.querySelector('#cal-start');
    const end = root.querySelector('#cal-end');
    const labels = [root.querySelector('label[for="cal-start"]'), root.querySelector('label[for="cal-end"]')];

    toggle.addEventListener('change', () => {
        const allDay = toggle.checked;
        const firstDay = start.value.slice(0, 10);
        const lastDay = end.value.slice(0, 10) || firstDay;
        start.type = end.type = allDay ? 'date' : 'datetime-local';
        start.value = allDay ? firstDay : (firstDay ? `${firstDay}T09:00` : '');
        end.value = allDay ? lastDay : (lastDay ? `${lastDay}T10:00` : '');
        labels[0].textContent = allDay ? 'First day' : 'Starts';
        labels[1].textContent = allDay ? 'Last day' : 'Ends';
    });

    // Moving the start past the end drags the end along, keeping the length, rather than leaving an impossible range.
    start.addEventListener('change', () => {
        if (!start.value || !end.value) return;
        if (toggle.checked) {
            if (end.value < start.value) end.value = start.value;
        } else if (new Date(end.value) <= new Date(start.value)) {
            end.value = toLocalInputValue(new Date(new Date(start.value).getTime() + 60 * 60_000));
        }
    });
}

/** The gallery's "everyone or specific people" behaviour: ticking everyone mutes the list without clearing it. */
function wireVisibility(root) {
    const everyone = root.querySelector('#cal-shared');
    if (!everyone) return;
    const hint = root.querySelector('#cal-share-hint');
    const list = root.querySelector('.share-people-list');
    const boxes = [...root.querySelectorAll('.share-person-check')];

    const sync = () => {
        boxes.forEach((box) => { box.disabled = everyone.checked; });
        list?.classList.toggle('is-muted', everyone.checked);
        hint.textContent = everyone.checked
            ? 'The whole family can see this. The names below apply again if you turn this off.'
            : boxes.some((b) => b.checked) ? 'Only you and the people you tick will see it.' : 'Only you will see it, until you tick someone.';
    };
    everyone.addEventListener('change', sync);
    boxes.forEach((box) => box.addEventListener('change', sync));
    sync();
}

// ---------------------------------------------------------------------------- Add to calendar

/**
 * Downloads one event as an .ics file. It is fetched with the bearer token and saved from a
 * blob, because a plain link could not carry the token.
 */
async function downloadIcs(id) {
    try {
        const text = await api.getEventIcs(id);
        const url = URL.createObjectURL(new Blob([text], { type: 'text/calendar;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `event-${id}.ics`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // After the click has been handled: revoking synchronously can cancel the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
        showToast(err.message ?? 'Could not download that event', 'error');
    }
}

// ---------------------------------------------------------------------------- Subscribe panel

async function loadToken() {
    try {
        state.token = await api.getCalendarToken();
    } catch (err) {
        document.getElementById('cal-subscribe-content').innerHTML = `<p class="form-error">${escapeHtml(err.message)}</p>`;
        return;
    }
    drawSubscribe();
}

function drawSubscribe() {
    const content = document.getElementById('cal-subscribe-content');
    if (content) content.innerHTML = renderSubscribe(state.token);
}

async function onSubscribeClick(e) {
    const button = e.target.closest('button');
    const field = e.target.closest('#cal-url, #cal-url-https');
    if (field) return field.select();
    if (!button) return;

    if (button.id === 'cal-url-copy') return void copyLink(button);

    const confirmations = {
        'cal-token-regenerate': 'Make a new link? Your current link stops working straight away, so any calendar already subscribed to it will need the new one.',
        'cal-token-revoke': 'Stop sharing this link? Calendars already subscribed to it will stop updating. You can make a new link any time.',
    };
    if (confirmations[button.id] && !confirm(confirmations[button.id])) return;

    const actions = {
        'cal-token-create': () => api.createCalendarToken(),
        'cal-token-regenerate': () => api.createCalendarToken(),
        'cal-token-revoke': () => api.revokeCalendarToken(),
    };
    const act = actions[button.id];
    if (!act) return;

    button.disabled = true;
    try {
        state.token = await act();
        drawSubscribe();
        showToast(button.id === 'cal-token-revoke' ? 'Link switched off' : 'Your link is ready', 'success');
    } catch (err) {
        showToast(err.message ?? 'Something went wrong', 'error');
        button.disabled = false;
    }
}

async function copyLink(button) {
    const input = document.getElementById('cal-url');
    try {
        await navigator.clipboard.writeText(input.value);
        showToast('Link copied', 'success');
    } catch {
        // No clipboard permission (or an insecure context): select it so Ctrl+C or a long press does the job.
        input.select();
        showToast('Press Ctrl+C (or Cmd+C) to copy the selected link', 'info');
    }
    button.blur();
}
